import { useEffect, useRef } from "react";
import "./style.scss";
import "./style.mobile.scss";
import geoCardTechnicalSeo from "@/assets/images/geo/geo_card_technical_seo-282feb.png";
import geoCardGeoDiscovery from "@/assets/images/geo/geo_card_geo_discovery-64c89c.png";
import geoCardGeoDiscoveryMobile from "@/assets/images/geo/geo_card_geo_discovery_mobile-4ead74.png";
import geoCardCatalog from "@/assets/images/geo/geo_card_catalog-1b6105.png";
import geoCardZeroTech from "@/assets/images/geo/geo_card_zero_tech-21f5cf.png";
import geoCardZeroTechMobile from "@/assets/images/geo/geo_card_zero_tech_mobile-592b3a.png";
import geoCardTechnicalSeoBlur from "@/assets/images/geo/geo_card_technical_seo_blur-73cf93.png";
import geoCardNav from "@/assets/images/geo/geo_card_nav.svg";
import geoStepsTimeline from "@/assets/images/geo/geo_steps_timeline.svg";
import classNames from "classnames";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components";
import { useMobile } from "@/hooks";
import { scrollIntoView } from "@/utils";
import emitter from "@/event";
import { trackContactClick } from "@/lib/analytics";

const geoCards = [
  {
    key: "technical-seo",
    title: "Technical SEO",
    description:
      "Optimized product feeds and structured data to rank higher and drive more traffic across multiple platforms.",
    descriptionMobile:
      "Structured data foundation, entity clarity pages, UCP/ACP enrichments + dynamic live feeds",
    image: geoCardTechnicalSeo,
    blur: geoCardTechnicalSeoBlur,
    showNav: true,
  },
  {
    key: "geo-discovery",
    title: "GEO for AI Discovery & Citations",
    description:
      "Get your brand and products cited in ChatGPT, Gemini, Perplexity, Google AI Overview, and Google Shopping. and other AI shopping agents.",
    image: geoCardGeoDiscovery,
    imageMobile: geoCardGeoDiscoveryMobile,
  },
  {
    key: "catalog",
    title: "Catalog Integration & Optimization",
    description:
      "Connecting and refining product data into a clean, unified, high-performing catalog format and feed to multiple platforms.",
    image: geoCardCatalog,
  },
  {
    key: "zero-tech",
    title: "Zero-Tech Setup & Ongoing Optimization",
    description:
      "Seamless integration with your store with SEO, GEO, dynamic feeds, trust signals, and monthly updates.",
    descriptionMobile:
      "Dedicated support, citations/impressions tracking, agent referral & conversion attribution",
    image: geoCardZeroTech,
    imageMobile: geoCardZeroTechMobile,
  },
] as const;

const geoSteps = [
  {
    title: "1. Connect your store",
    description:
      "Quick integration with Shopify, WooCommerce, or custom catalog.",
  },
  {
    title: "2. We optimize",
    description:
      "Product data, images, structured data, and GEO signals across platforms.",
  },
  {
    title: "3. Go live",
    description: "Appear in Google Shopping, Pinterest, Bing & AI answers.",
  },
  {
    title: "4. Track & grow",
    description: "Monthly reports and expert recommendations.",
  },
] as const;

const geoFaqs = [
  {
    question: "Is this suitable for small stores?",
    answer: (
      <>
        <p>
          Yes. While BorderX Lab is known for massive retailers, our CloudStore
          AI and GEO (Generative Engine Optimization) services are highly
          effective for smaller US stores.
        </p>
        <p>
          <strong>- The &quot;Small Store&quot; Advantage:</strong> In the US
          market, AI search engines (like Google Gemini) prioritize context and
          structured data over raw backlink volume. For a small store, this
          service levels the playing field by ensuring your catalog is more
          &quot;readable&quot; to AI than your larger, slower-moving
          competitors.
        </p>
        <p>
          <strong>- Requirement:</strong> It is best suited for stores that
          already have a digital catalog and want to move beyond basic SEO into
          AI-driven discovery.
        </p>
      </>
    ),
  },
  {
    question: "How long does integration take?",
    answer: (
      <>
        <p>
          <strong>7 to 14 days.</strong> Since you are linking to the multiple
          platforms, the process is streamlined:
        </p>
        <p>
          <strong>- Catalog Sync:</strong> Connecting your existing store
          (Shopify, BigCommerce, etc.) to their AI optimizer takes a few days.
        </p>
        <p>
          <strong>- Catalog Optimization:</strong> Our engine spent the next
          week &quot;ingesting&quot; your data to create the rich schema and
          structured entities needed for AI features.
        </p>
      </>
    ),
  },
  {
    question: "Do you work with multiple platforms and LLMs?",
    answer: (
      <>
        <p>
          <strong>Yes, and specifically for AI Overviews.</strong> In the US
          market, simply having a feed isn&apos;t enough anymore. Our service
          focuses on two &quot;Google&quot; fronts:
        </p>
        <p>
          <strong>- Platform Shopping Optimization:</strong> We will clean and
          enrich your product attributes (color, style, material, size, etc.) to
          improve your &quot;Quality Score,&quot; which lowers your
          Cost-Per-Click (CPC).
        </p>
        <p>
          <strong>- GEO for LLMs:</strong> We implement{" "}
          <strong>Schema markup</strong> that tells search engines exactly what
          your products are. This increases the chance that your store is cited
          as a source in <strong>AI Overviews</strong> (the AI-generated answers
          at the top of search results), also applicable for other LLMs.
        </p>
      </>
    ),
  },
] as const;

const Geo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile] = useMobile();
  const el = useRef<HTMLElement>(null);

  useEffect(() => {
    const scrollHandler = () => {
      scrollIntoView(el.current as HTMLDivElement);
    };
    emitter.on("SCROLL_TO_GEO", scrollHandler);
    return () => {
      emitter.off("SCROLL_TO_GEO", scrollHandler);
    };
  }, []);

  useEffect(() => {
    if (location.pathname === "/geo") {
      setTimeout(() => {
        scrollIntoView(el.current as HTMLDivElement);
      }, 20);
    }
  }, [location.pathname]);

  return (
    <section className="geo" ref={el} aria-labelledby="geo-brand-heading">
      <header className="geo-intro">
        <h2 id="geo-brand-heading" className="geo-brand t-a-c">
          SEO and
          <span className="geo-brand-highlight">
            &nbsp;GEO for&nbsp;
            <span className="geo-brand-g-dot" aria-hidden />
          </span>
          more AI Visibility
        </h2>
        <p className="geo-headline t-a-c">
          We help small and medium e-commerce businesses get more ready-to-buy
          customers from multiple platforms, and AI agents — with catalog
          integration and zero technical barriers.
        </p>
      </header>
      <p className="geo-sub t-a-c">
        Specialized Generative Engine Optimization focused on AI
        discoverability, citation in generative answers (Gemini, ChatGPT,
        Perplexity, Google AI Overview, and Google Shopping, etc.), and
        agent-ready transactability
      </p>

      <h3 className="geo-section-title t-a-c">
        What You Get – Real Traffic &amp; Results
      </h3>
      <ul className="geo-cards">
        {geoCards.map((card) => {
          const description =
            isMobile && "descriptionMobile" in card && card.descriptionMobile
              ? card.descriptionMobile
              : card.description;
          const image =
            isMobile && "imageMobile" in card && card.imageMobile
              ? card.imageMobile
              : card.image;

          return (
            <li
              key={card.key}
              className={classNames("geo-card", `geo-card--${card.key}`)}
            >
              <div className="geo-card-visual">
                {!isMobile && "blur" in card && card.blur && (
                  <img
                    className="geo-card-blur"
                    src={card.blur}
                    alt=""
                    aria-hidden
                  />
                )}
                <div className="geo-card-visual-inner">
                  <img
                    className="geo-card-img"
                    src={image}
                    alt=""
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
              <div className="geo-card-content">
                <h4 className="geo-card-title">{card.title}</h4>
                <p className="geo-card-desc">{description}</p>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="geo-bottom">
        <h3 className="geo-section-title t-a-c">Get Started in Minutes</h3>
        <div className="geo-steps">
          <img
            className="geo-steps-timeline"
            src={geoStepsTimeline}
            alt=""
            aria-hidden
          />
          <ol className="geo-steps-list">
            {geoSteps.map((step) => (
              <li key={step.title} className="geo-step">
                <h4 className="geo-step-title">{step.title}</h4>
                <p className="geo-step-desc">{step.description}</p>
              </li>
            ))}
          </ol>
        </div>

        <h3 className="geo-section-title t-a-c">FAQ</h3>
        <div className="geo-faq-list">
          {geoFaqs.map((item, index) => (
            <div key={item.question} className="geo-faq-group">
              <article className="geo-faq-item">
                <div className="geo-faq-row geo-faq-row--question">
                  <span className="geo-faq-label geo-faq-label--q">Q.</span>
                  <h4 className="geo-faq-question">{item.question}</h4>
                </div>
                <div className="geo-faq-row geo-faq-row--answer">
                  <span className="geo-faq-label geo-faq-label--a">A.</span>
                  <div className="geo-faq-answer">{item.answer}</div>
                </div>
              </article>
              {index < geoFaqs.length - 1 && (
                <hr className="geo-faq-divider" aria-hidden />
              )}
            </div>
          ))}
        </div>

        <div className="geo-cta">
          <p className="geo-trust t-a-c">
            No credit card required • Cancel anytime
            <br />
            Trusted by 500+ e-commerce merchants and brands • 5M+ orders powered
            • $1B+ GMV fulfilled
          </p>
          <div className="geo-contact-us t-a-c">
            <Button
              onClick={() => {
                trackContactClick("GEO");
                navigate("/contact-us");
              }}
            >
              Contact Us
            </Button>
          </div>
          <p className="geo-trial t-a-c">
            <span className="geo-trial-line" aria-hidden />
            Start Your Free 1-Month Trial
            <span className="geo-trial-line" aria-hidden />
          </p>
        </div>
      </div>
    </section>
  );
};

export default Geo;
